import { ArrowRight, Camera, MapPin, Globe, Award } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import useAnalytics from '../hooks/useAnalytics';

const About = () => {
  useAnalytics('about');
  const expertise = [
    { icon: Camera, title: 'Photography', description: 'Editorial and documentary photography focused on culture and landscapes' },
    { icon: MapPin, title: 'Destination Coverage', description: 'In-depth travel guides and destination features' },
    { icon: Globe, title: 'Cultural Stories', description: 'Long-form narratives exploring traditions and communities' },
    { icon: Award, title: 'Adventure Content', description: 'Trekking guides, expedition reports, and outdoor adventures' },
  ];

  return (
    <div className="min-h-screen bg-stone-50 pt-20" data-testid="about-page">
      <SEO
        title="About"
        description="Learn about Marta Scharle, a travel journalist and photographer sharing authentic stories from Nepal and the Himalayas."
        url="/about"
      />
      {/* Hero Section */}
      <section className="py-16 md:py-24 border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <p className="text-sm tracking-widest uppercase text-red-800 font-medium mb-4">
                About the Author
              </p>
              <h1 className="font-serif text-5xl md:text-6xl text-stone-900 tracking-tight mb-8">
                Telling Nepal's Stories to the World
              </h1>
              <p className="text-stone-600 text-lg leading-relaxed mb-6">
                I'm an aspiring travel journalist and photographer dedicated 
                to sharing authentic stories from Nepal and the greater Himalayan region.
              </p>
              <p className="text-stone-600 leading-relaxed mb-8">
                My work focuses on the intersection of adventure, culture, and everyday life 
                — from remote mountain villages to bustling urban bazaars. I believe every 
                journey holds a story worth telling.
              </p>
              <Link
                to="/contact#contact-info"
                className="inline-flex items-center gap-2 bg-stone-900 text-stone-50 px-8 py-4 rounded-sm hover:bg-red-800 transition-colors duration-300 font-medium tracking-wide text-sm uppercase"
                data-testid="about-contact-btn"
              >
                Work With Me <ArrowRight size={16} />
              </Link>
            </div>
            <div className="relative">
              <div className="overflow-hidden rounded-sm">
                <img
                  src="https://customer-assets.emergentagent.com/job_kathmandu-lens/artifacts/88wovhd8_martainnepal2.JPG"
                  alt="Marta Scharle - Travel Writer"
                  loading="lazy"
                  className="w-full aspect-square object-cover object-center filter saturate-[0.95] contrast-[1.02] brightness-[1.01]"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 w-40 h-40 bg-amber-500 rounded-sm -z-10"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="text-center mb-16">
            <p className="text-sm tracking-widest uppercase text-red-800 font-medium mb-4">
              What I Do
            </p>
            <h2 className="font-serif text-4xl md:text-5xl text-stone-900 tracking-tight">
              Areas of Expertise
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {expertise.map((item, index) => (
              <div
                key={index}
                className="p-8 bg-stone-50 rounded-sm hover:shadow-lg transition-shadow duration-300"
                data-testid={`expertise-item-${index}`}
              >
                <div className="w-14 h-14 rounded-full bg-stone-100 flex items-center justify-center mb-6">
                  <item.icon size={24} className="text-red-800" />
                </div>
                <h3 className="font-serif text-xl text-stone-900 mb-3">{item.title}</h3>
                <p className="text-stone-600 text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-24 md:py-32 bg-stone-50">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-20">
          <p className="text-sm tracking-widest uppercase text-red-800 font-medium mb-4">
            My Journey
          </p>
          <h2 className="font-serif text-4xl md:text-5xl text-stone-900 tracking-tight mb-12">
            The Path That Led Here
          </h2>
          <div className="prose prose-stone prose-lg max-w-none">
            <p className="text-stone-600 leading-relaxed mb-6">
              When I first arrived in Nepal, backpack in tow, I expected to stay for a few weeks.
              The mountains had other plans. What began as a helicopter trip to Annapurna 
              Base Camp became a lifelong fascination with the people, cultures, and landscapes 
              of the Himalayas.
            </p>
            <p className="text-stone-600 leading-relaxed mb-6">
              As an aspiring journalist, I plan to hike every major trail in Nepal, document festivals 
              from Dashain to Tihar, and build relationships with Himalayan communities from Annapurna
              to Everest. Each story I write is informed by these experiences and the trust I earn
              from the people who call these places home.
            </p>
            <p className="text-stone-600 leading-relaxed mb-6">
              Today, I'm working to bring Nepal's stories to global audiences. 
              Whether it's a feature on ancient temple traditions, a guide to 
              off-the-beaten-path treks, or a photo essay on daily life in Kathmandu, my goal 
              is always the same: to show Nepal as it truly is, in all its complexity and beauty.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-stone-900">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 text-center">
          <h2 className="font-serif text-4xl md:text-5xl text-stone-50 tracking-tight mb-6">
            Ready to Collaborate?
          </h2>
          <p className="text-stone-400 text-lg max-w-2xl mx-auto mb-10">
            I'm available for assignments, editorial partnerships, and destination coverage 
            throughout Nepal and the Himalayan region.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/contact#contact-info"
              className="inline-flex items-center gap-2 bg-stone-50 text-stone-900 px-10 py-4 rounded-sm hover:bg-amber-500 transition-colors duration-300 font-medium tracking-wide text-sm uppercase"
              data-testid="about-cta-contact-btn"
            >
              Get in Touch <ArrowRight size={16} />
            </Link>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 bg-transparent border border-stone-50/30 text-stone-50 px-10 py-4 rounded-sm hover:border-stone-50 transition-all duration-300 text-sm tracking-wide uppercase"
            >
              View My Work
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
